import { cn } from '@/src/utils';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

interface DndItemProps {
  index: number;
  moveCardHandler: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
  className?: string;
}

export default function DndItem({
  index,
  moveCardHandler,
  children,
  className,
}: DndItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  // 마우스가 hover item 높이의 절반을 넘을 경우에만 이동을 수행하도록 설계
  // - 아래로 드래그할 때 커서가 50% 이하일 때만 이동
  // - 위로 드래그할 때 커서가 50% 이상일 때만 이동
  const [, drop] = useDrop({
    accept: 'CARD',
    hover(item: { index: number; name: string }, monitor: any) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // 아래로 드래깅
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // 위로 드래깅
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCardHandler(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'CARD',
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={cn('movable-item', className)}
      style={{ opacity }}
    >
      {children}
    </div>
  );
}
